import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Eye,
  History,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";

interface GalleryPhoto {
  url: string;
  caption: string;
}

interface EventAlbum {
  id: string;
  title: string;
  date: string;
  category: string;
  description: string;
  photos: GalleryPhoto[];
}

const albums: EventAlbum[] = [
  {
    id: "48e-anniversaire",
    title: "Action de Grâce — 48e Anniversaire",
    date: "12 Octobre 2025",
    category: "Célébration",
    description:
      "Célébration solennelle du 48e anniversaire de l'Église de Dieu Garizim. Un grand moment de gratitude fraternelle, de louange fervente et de prédication au sanctuaire.",
    photos: [
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/actions-de-graces-2025/sanctuaire-1.jpg",
        caption: "Le sanctuaire paré pour les célébrations du 48e anniversaire",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/actions-de-graces-2025/sanctuaire-1.1.jpg",
        caption:
          "L'assemblée unie dans un même cœur lors de l'ouverture du culte",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/actions-de-graces-2025/sanctuaire-2.jpg",
        caption:
          "Moment d'intercession, de reconnaissance et de prière solennelle",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/actions-de-graces-2025/sanctuaire-3.jpg",
        caption: "Proclamation inspirante de la Parole de Dieu",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/actions-de-graces-2025/sanctuaire-4.jpg",
        caption:
          "La chorale et les musiciens guidant la congrégation dans la louange",
      },
    ],
  },
  {
    id: "evolution-construction",
    title: "Garizim Évolution — Histoire & Construction",
    date: "Archives Patrimoniales",
    category: "Histoire",
    description:
      "Rétrospective photographique retraçant les différentes étapes de la construction et de l'aménagement du temple de Garizim à Montréal.",
    photos: [
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/%C3%89volution/Garizim%201.jpg?updatedAt=1760916858578",
        caption: "Premiers jalons du bâtiment de l'église",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/%C3%89volution/Garizim%202.jpg?updatedAt=1760916858529",
        caption: "Début des travaux de charpente et gros œuvre",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/%C3%89volution/garizim%203%20-construction%20-%202.jpg?updatedAt=1760916858537",
        caption: "Élévation de la toiture et des structures métalliques",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/%C3%89volution/Garizim-4%20-%20sanctuaire%20-%201.jpg?updatedAt=1760916858554",
        caption: "Installation des gradins et aménagement intérieur",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/%C3%89volution/garizim%203%20-construction%20-%203.jpg?updatedAt=1760916858485",
        caption: "Pose des panneaux d'isolation et traitement acoustique",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/%C3%89volution/garizim%203%20-construction%20-%201.jpg?updatedAt=1760916858477",
        caption: "Progression des finitions de la nef principale",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/%C3%89volution/Garizim-4%20-%20sanctuaire%20-%204.jpg?updatedAt=1760916858470",
        caption: "Finition soignée du pupitre et de la plateforme pastorale",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/%C3%89volution/Garizim-4%20-%20sanctuaire%20-%202.jpg?updatedAt=1760916858473",
        caption: "Vue panoramique des travées et des sièges du sanctuaire",
      },
      {
        url: "https://ik.imagekit.io/4apzi8lz0/egarizim/%C3%89volution/Garizim-4.jpg?updatedAt=1760916858236",
        caption:
          "Inauguration du sanctuaire achevé dédié au culte du Très-Haut",
      },
    ],
  },
];

export default function PageEvents() {
  const [activeAlbumId, setActiveAlbumId] = useState<string>("all");
  const [lightboxState, setLightboxState] = useState<{
    isOpen: boolean;
    photos: GalleryPhoto[];
    currentIndex: number;
    title: string;
  }>({
    isOpen: false,
    photos: [],
    currentIndex: 0,
    title: "",
  });

  // Handle keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxState.isOpen) return;

      if (e.key === "Escape") {
        setLightboxState((prev) => ({ ...prev, isOpen: false }));
      } else if (e.key === "ArrowLeft") {
        setLightboxState((prev) => ({
          ...prev,
          currentIndex:
            prev.currentIndex > 0
              ? prev.currentIndex - 1
              : prev.photos.length - 1,
        }));
      } else if (e.key === "ArrowRight") {
        setLightboxState((prev) => ({
          ...prev,
          currentIndex:
            prev.currentIndex < prev.photos.length - 1
              ? prev.currentIndex + 1
              : 0,
        }));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxState.isOpen]);

  const openLightbox = (
    albumPhotos: GalleryPhoto[],
    index: number,
    albumTitle: string,
  ) => {
    setLightboxState({
      isOpen: true,
      photos: albumPhotos,
      currentIndex: index,
      title: albumTitle,
    });
  };

  const displayedAlbums =
    activeAlbumId === "all"
      ? albums
      : albums.filter((a) => a.id === activeAlbumId);

  return (
    <div className="min-h-screen bg-[#FDFCFB] text-slate-900 font-sans flex flex-col antialiased">
      <Header />

      <main className="flex-1">
        {/* HERO HEADER */}
        <section className="relative pt-20 pb-8 sm:pt-32 sm:pb-20 md:pt-40 md:pb-28 bg-slate-950 text-white overflow-hidden border-b border-slate-900">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/25 via-transparent to-transparent pointer-events-none" />

          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 sm:px-3.5 sm:py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-[11px] sm:text-xs font-semibold uppercase tracking-widest text-church-gold-400 mb-3 sm:mb-6">
                <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-church-gold-400" />
                <span>Mémoire &amp; Actualités</span>
              </div>

              <h1 className="text-2xl sm:text-4xl lg:text-6xl font-bold tracking-tight text-white mb-2 sm:mb-6 leading-tight">
                Événements &amp; Galerie Historique
              </h1>

              <p className="text-xs sm:text-lg lg:text-xl text-slate-300 leading-relaxed font-normal line-clamp-3 sm:line-clamp-none">
                Revivez les temps forts de notre assemblée : jubilés d'action de
                grâce, rassemblements mémorables et les grandes étapes de la
                bâtisse de notre sanctuaire à la gloire de Dieu.
              </p>
            </div>
          </div>
        </section>

        {/* ALBUMS CONTENT */}
        <section className="py-8 sm:py-16 md:py-24 bg-[#FDFCFB]">
          <div className="container mx-auto px-4 sm:px-6">
            {/* Filter Buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 pb-4 sm:pb-8 border-b border-slate-200 mb-6 sm:mb-12">
              <div>
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-church-gold-600 block mb-0.5 sm:mb-1">
                  Sélection d'Albums
                </span>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-950">
                  Nos Collections Photos
                </h2>
              </div>

              <div className="overflow-x-auto pb-1 sm:pb-0">
                <div className="inline-flex p-1 rounded-xl sm:rounded-2xl bg-slate-100 border border-slate-200 whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => setActiveAlbumId("all")}
                    className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                      activeAlbumId === "all"
                        ? "bg-white text-slate-950 shadow-sm"
                        : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    Tous les albums
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveAlbumId("48e-anniversaire")}
                    className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                      activeAlbumId === "48e-anniversaire"
                        ? "bg-white text-slate-950 shadow-sm"
                        : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    48e Anniversaire
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveAlbumId("evolution-construction")}
                    className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                      activeAlbumId === "evolution-construction"
                        ? "bg-white text-slate-950 shadow-sm"
                        : "text-slate-600 hover:text-slate-950"
                    }`}
                  >
                    Histoire &amp; Bâtisse
                  </button>
                </div>
              </div>
            </div>

            {/* Render Albums */}
            <div className="space-y-10 sm:space-y-20">
              {displayedAlbums.map((album) => (
                <div key={album.id} className="scroll-mt-32">
                  <div className="mb-4 sm:mb-8">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1.5 sm:mb-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wider bg-church-blue-50 text-church-blue-900 border border-church-blue-200/60">
                        {album.category === "Histoire" ? (
                          <History className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        ) : (
                          <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        )}
                        {album.date}
                      </span>
                      <span className="text-[11px] sm:text-xs text-slate-500 font-medium">
                        {album.photos.length} photos
                      </span>
                    </div>

                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-950 tracking-tight mb-1.5 sm:mb-3">
                      {album.title}
                    </h3>
                    <p className="text-slate-600 text-xs sm:text-sm md:text-base max-w-3xl leading-relaxed line-clamp-2 sm:line-clamp-none">
                      {album.description}
                    </p>
                  </div>

                  {/* Photos Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-6">
                    {album.photos.map((photo, idx) => (
                      <div
                        key={idx}
                        onClick={() =>
                          openLightbox(album.photos, idx, album.title)
                        }
                        className="group relative aspect-[4/3] rounded-xl sm:rounded-2xl p-1 sm:p-1.5 bg-white border sm:border-2 border-slate-300 hover:border-church-blue-800 shadow-sm hover:shadow-2xl transition-all duration-300 cursor-pointer flex flex-col"
                      >
                        <div className="w-full h-full rounded-lg sm:rounded-xl overflow-hidden relative">
                          <img
                            src={photo.url}
                            alt={photo.caption}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-2.5 sm:p-3.5">
                            <p className="text-white text-[11px] sm:text-xs font-medium line-clamp-2 leading-snug drop-shadow-sm">
                              {photo.caption}
                            </p>
                            <div className="mt-1 sm:mt-1.5 inline-flex items-center gap-1 text-[10px] sm:text-[11px] text-church-gold-300 font-semibold">
                              <Eye className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              <span>Agrandir</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* LIGHTBOX MODAL */}
        {lightboxState.isOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-200"
            onClick={() =>
              setLightboxState((prev) => ({ ...prev, isOpen: false }))
            }
          >
            <div
              className="relative max-w-5xl w-full max-h-[92vh] flex flex-col items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                type="button"
                onClick={() =>
                  setLightboxState((prev) => ({ ...prev, isOpen: false }))
                }
                className="absolute -top-12 right-0 sm:top-2 sm:right-2 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-md transition-colors"
                aria-label="Fermer"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Prev / Next Buttons */}
              <button
                type="button"
                onClick={() =>
                  setLightboxState((prev) => ({
                    ...prev,
                    currentIndex:
                      prev.currentIndex > 0
                        ? prev.currentIndex - 1
                        : prev.photos.length - 1,
                  }))
                }
                className="absolute left-2 sm:-left-14 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-md transition-colors"
                aria-label="Photo précédente"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                type="button"
                onClick={() =>
                  setLightboxState((prev) => ({
                    ...prev,
                    currentIndex:
                      prev.currentIndex < prev.photos.length - 1
                        ? prev.currentIndex + 1
                        : 0,
                  }))
                }
                className="absolute right-2 sm:-right-14 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-md transition-colors"
                aria-label="Photo suivante"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Image Container */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black border-4 border-white/90 max-h-[78vh] flex items-center justify-center">
                <img
                  src={lightboxState.photos[lightboxState.currentIndex]?.url}
                  alt={
                    lightboxState.photos[lightboxState.currentIndex]?.caption
                  }
                  className="max-h-[76vh] w-auto max-w-full object-contain"
                />
              </div>

              {/* Caption & Counter */}
              <div className="mt-4 text-center max-w-2xl px-4">
                <p className="text-white text-sm sm:text-base font-medium drop-shadow-sm mb-1">
                  {lightboxState.photos[lightboxState.currentIndex]?.caption}
                </p>
                <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
                  <span>{lightboxState.title}</span>
                  <span>•</span>
                  <span>
                    {lightboxState.currentIndex + 1} /{" "}
                    {lightboxState.photos.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
